"use strict";

import Money from 'js-money/lib';
import moment from "moment";
import { getPackingLocations } from "../api/packing/packing";

const today = moment();

export default {
    state: {
        salesResource: {},
        customersResource: {},
        availableOrderTypes: {
            "Awaiting Postage": "awaiting",
            "Shop Sales": "shop",
            "Unpaid": "unpaid",
            "Sent": "sent",
            "Backorders": "backorder",
            "Old Tills" : 'old',
            "Archived": "archived",
            "Emailed Orders": "emailed",
        },
        salesFilter: {
            orderType: 'awaiting',
            page: 1
        },
        filterRequest: {},
        displayedSales: {
            data: [],
            prev_page_url: null,
            next_page_url: null,
            last_page: 0
        },
        counts: {},
        currentOrder: {
            customer: {
                full_name: ''
            },
            total: {
                amount: 0
            },
            postage: {
                amount: 0
            },
            items: []
        },
        collections: [],
        currentCollectionsDay: today.startOf( 'day' ),
        packingLocations: []
    },

    getters: {
        salesResource: ( state ) => {
            return state.salesResource;
        },

        customersResource: ( state ) => {
            return state.customersResource;
        },

        availableOrderTypes: ( state ) => {
            return state.availableOrderTypes;
        },

        salesFilter: ( state ) => {
            return state.salesFilter;
        },

        displayedSales: ( state ) => {
            return state.displayedSales;
        },

        counts: ( state ) => {
            return state.counts;
        },

        currentOrder: ( state ) => {
            return state.currentOrder;
        },

        collections: (state) => {
            return state.collections;
        },

        currentCollectionsDay: (state) => {
            return state.currentCollectionsDay;
        },

        packingLocations: (state) => {
            return state.packingLocations;
        }
    },

    mutations: {
        salesResource: ( state, resource ) => {
            state.salesResource = resource;
        },

        customersResource: ( state, resource ) => {
            state.customersResource = resource;
        },

        salesFilter: ( state, filter ) => {
            state.salesFilter = filter
        },

        salesFilterPage: ( state, page ) => {
            state.salesFilter.page = ( page < 1 ) ? 1 : page;
        },

        displayedSales: ( state, sales ) => {
            state.displayedSales = sales;
        },

        currentOrder: ( state, order ) => {
            state.currentOrder = order;
        },

        currentOrderItems: ( state, items ) => {
            state.currentOrder.items = items;
        },

        setSalesOrderTotal: ( state, newTotal ) => {
            state.currentOrder.total.amount = newTotal.amount;
        },

        collections: (state, collections) => {
            state.collections = collections;
        },

        currentCollectionsDay: (state, newCollectionsDay) => {
            state.currentCollectionsDay = newCollectionsDay;
        }
    },

    actions: {
        salesFilter: ( { getters, state, commit }, newFilter ) => {
            let filter = ( newFilter === null ) ? getters.salesFilter : newFilter;
            getters.salesResource.filter( {
                filter: filter,
                page: filter.page,

                // use before callback
                before( request ) {

                    // abort previous request, if exists
                    if ( getters.filterRequest ) {
                        getters.filterRequest.abort();
                    }

                    // set previous request on Vue instance
                    state.filterRequest = request;
                }

            } ).then(
                ( data ) => {
                    commit( 'salesFilter', filter );
                    commit( 'displayedSales', data.body );
                },
                ( error ) => {
                }
            );
        },

        salesCounts: ( { state } ) => {
            state.salesResource.salesCounts().then(
                ( data ) => {
                    state.counts = data.body;
                }
            )
        },

        currentOrder: ( { state, commit }, salesId ) => {
            state.salesResource.get( { 'salesId': salesId } ).then(
                ( data ) => {
                    state.currentOrder = data.body
                }
            )
        },

        togglePaid: ( { state }, data ) => {
            return state.salesResource.toggleAction( { salesId: data.salesId, action: 'paid' }, { notes: data.notes } );
        },

        toggleSent: ( { state }, salesId ) => {
            return state.salesResource.toggleAction( { salesId: salesId, action: 'sent' }, {} );
        },

        toggleBackorder: ( { state }, salesId ) => {
            return state.salesResource.toggleAction( { salesId: salesId, action: 'backorder' }, {} );
        },

        updateCustomer: ( { getters, commit }, customer ) => {
            return new Promise( ( resolve, reject ) => {
                getters.customersResource.update( { customerId: customer.id }, customer ).then(
                    ( response ) => {
                        resolve( response.data );
                    },

                    () => {
                        reject( 'Problem updating customer' );
                    }
                )
            } );
        },

        removeSaleItem: ( { getters, commit, dispatch }, itemId ) => {
            return new Promise( ( resolve, reject ) => {
                getters.salesResource.removeSalesOrderItem(
                    {
                        salesId: getters.currentOrder.id,
                        itemId: itemId
                    }
                ).then(
                    ( sale ) => {
                        dispatch( 'updateOrderTotal' );

                        commit( 'currentOrderItems', _.filter( getters.currentOrder.items, function ( item ) {
                            return item.id !== itemId
                        } ) );

                        resolve( sale.data );
                    },

                    () => {
                        reject( 'Problem deleting item' )
                    }
                )
            } );

        },

        moveSaleItem( { getters, commit, dispatch }, data ) {
            return new Promise( ( resolve, reject ) => {
                    getters
                        .salesResource
                        .moveSalesOrderItem( data, {} )
                        .then(
                            ( response ) => {
                                resolve( response.data );
                            },
                            ( error ) => {
                                reject( 'Problem moving item' );
                            }
                        );
                }
            );
        },

        updateSalesOrderItem: ( { state, dispatch }, itemData ) => {
            return new Promise( ( resolve, reject ) => {
                state.salesResource.updateSalesOrderItem(
                    {
                        salesId: itemData.order_id,
                        itemId: itemData.id
                    },
                    {
                        quantity: itemData.quantity,
                        cost: parseInt( itemData.item_cost )
                    }
                )
                    .then(
                        ( item ) => {
                            _.$update( state.currentOrder.items, { 'id': item.body.id }, item.body );
                            dispatch( 'updateOrderTotal' );
                            resolve( item.body );
                        },
                        ( error ) => {
                            reject( error );
                        }
                    );
            } );

        },

        updateOrderTotal: ( { getters, commit } ) => {
            let total = new Money( 0, Money.GBP );
            _.forEach( getters.currentOrder.items, function ( item ) {
                let itemCost = new Money( parseInt( item.line_total ) * 100, Money.GBP );
                total = total.add( itemCost );
            } );
            commit( 'setSalesOrderTotal', total );
        },

        createOrder: ( { getters }, orderData ) => {
            return new Promise(
                ( resolve, reject ) => {
                    getters.salesResource.save(
                        {
                            order: orderData.order,
                            items: orderData.items,
                            customer: orderData.customer
                        }
                    ).then(
                        ( order ) => {
                            resolve( order.data );
                        },
                        ( error ) => {
                            reject( 'Problem creating order' );
                        }
                    )
                }
            )
        },

        setShopCart( { commit, state }, salesId ) {
            state.salesResource.get(
                {
                    salesId: salesId
                }
            ).then(
                ( cart ) => {
                    state.cartId = cart.body.id;
                    commit( 'setCart', cart.body );
                },
                ( error ) => {
                    reject( 'Problem setting shop cart' );
                }
            );
        },

        /**
         * Marks all items as taken in an order
         *
         * @param state
         * @param getters
         * @param commit
         * @param cart
         * @returns {Promise<any>}
         */
        takeAll: ( { state, getters, commit } ) => {
            return new Promise( ( resolve, reject ) => {
                getters.salesResource.takeAll( { salesId: getters.currentCart.id }, {} ).then(
                    ( cart ) => {
                        commit( 'setCart', cart.body );
                        resolve( cart.body );
                    },
                    ( error ) => {
                        reject( 'Problem marking items as taken' )
                    }
                )
            } );
        },

        clearCart( { commit, getters } ) {
            let salesResource = getters.salesResource;
            salesResource.clear( { salesId: getters.currentCart.id } ).then(
                ( cart ) => {
                    commit( 'setCart', cart.data );
                },
                ( error ) => {
                    console.log( error );
                }
            );
        },

        async getPackingLocations( {state} ) {
            state.packingLocations = ( await getPackingLocations() ).data
        },

        async updateCollection( {state, commit}, collection) {
            commit('collections', _.$update(state.collections, collection));
        }
    }
};
